import React, { useState, useEffect, useRef } from "react";
import { apiPostRequest } from "helpers/Requests";
import { toast } from "react-toastify";
import { FiEdit2, FiSave, FiX } from "react-icons/fi";

const DocumentComment = ({
  documentUuid,
  documentName,
  profileId,
  initialComment = "",
  isEditable = true,
  onCommentSaved,
}) => {
  const [comment, setComment] = useState(initialComment || "");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempComment, setTempComment] = useState(initialComment || "");
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, arrowPosition: 'top', arrowLeft: '20px' });
  const commentRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    setComment(initialComment || "");
    setTempComment(initialComment || "");
  }, [initialComment]);

  useEffect(() => {
    if (showTooltip && commentRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (commentRef.current && tooltipRef.current) {
          const rect = commentRef.current.getBoundingClientRect();
          const tooltipWidth = 300; // max-w-xs
          const tooltipHeight = tooltipRef.current.offsetHeight || 100; // fallback height
          
          // Position tooltip above the comment, aligned to the left of the comment cell
          // This ensures it stays within the comment column and doesn't cover action icons
          let left = rect.left;
          let top = rect.top - tooltipHeight - 8; // 8px margin above
          let arrowPosition = 'top';
          let arrowLeft = '20px'; // Arrow position from left (20px from left edge)
          
          // Ensure tooltip doesn't go off screen to the left
          if (left < 10) {
            left = 10;
            arrowLeft = `${rect.left - 10 + 20}px`; // Adjust arrow to point to comment
          }
          
          // Ensure tooltip doesn't extend beyond the comment column's right edge
          // This prevents it from covering the action column
          const commentColumnRight = rect.right;
          if (left + tooltipWidth > commentColumnRight - 10) {
            left = commentColumnRight - tooltipWidth - 10;
            if (left < rect.left) {
              // If tooltip is wider than comment column, center it but keep within screen
              left = Math.max(10, Math.min(rect.left, window.innerWidth - tooltipWidth - 10));
            }
            arrowLeft = `${rect.left + rect.width / 2 - left}px`; // Center arrow relative to comment
          }
          
          // If tooltip would go above viewport, show below instead
          if (top < 10) {
            top = rect.bottom + 8;
            arrowPosition = 'bottom';
          }
          
          setTooltipPosition({ top, left, arrowPosition, arrowLeft });
        }
      });
    }
  }, [showTooltip]);

  // Hide tooltip on scroll to prevent ellipses from appearing incorrectly
  useEffect(() => {
    const handleScroll = () => {
      if (showTooltip) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      // Listen to scroll events on window and all scrollable containers
      window.addEventListener('scroll', handleScroll, true);
      document.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        document.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [showTooltip]);

  const handleSave = async () => {
    if (!isEditable) return;

    setIsSaving(true);
    try {
      const response = await apiPostRequest("/hrm/employee-document/comment", {
        document_uuid: documentUuid,
        comment: tempComment || "",
      });

      if (response && response.status === 200) {
        setComment(tempComment);
        setIsEditing(false);
        toast.success("Comment saved successfully");
        if (onCommentSaved) {
          onCommentSaved(tempComment);
        }
      }
    } catch (error) {
      toast.error(error?.message || "Failed to save comment");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTempComment(comment);
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (!isEditable) return;
    setTempComment(comment);
    setIsEditing(true);
  };

  if (!isEditable && !comment) {
    return null;
  }

  return (
    <div className="w-full">
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            value={tempComment}
            onChange={(e) => setTempComment(e.target.value)}
            placeholder="Add comment..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={2}
            disabled={isSaving}
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSave className="w-3 h-3" />
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiX className="w-3 h-3" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="group relative">
          {comment ? (
            isEditable ? (
              <div className="flex items-start gap-2">
                <p className="flex-1 text-sm text-gray-700 px-2 py-1 bg-gray-50 rounded border border-gray-200">
                  {comment}
                </p>
                <button
                  onClick={handleEdit}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-500 hover:text-blue-600"
                  title="Edit comment"
                >
                  <FiEdit2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div 
                ref={commentRef}
                className="relative w-full max-w-full"
                onMouseEnter={() => {
                  if (comment && comment.length > 0) {
                    setShowTooltip(true);
                  }
                }}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <p 
                  className={`text-sm text-gray-700 cursor-pointer block w-full max-w-full overflow-hidden whitespace-nowrap hover:bg-white transition-colors ${
                    showTooltip ? 'truncate' : ''
                  }`}
                  style={{ 
                    maxWidth: '100%',
                    textOverflow: showTooltip ? 'ellipsis' : 'clip'
                  }}
                  title=""
                >
                  {showTooltip && comment.length > 25 ? `${comment.substring(0, 25)}...` : comment}
                </p>
                {showTooltip && comment && (
                  <div 
                    ref={tooltipRef}
                    className="fixed z-[9999] p-2 bg-gray-100 border border-gray-300 text-gray-800 text-xs rounded shadow-xl break-words whitespace-normal pointer-events-none"
                    style={{
                      maxWidth: '300px',
                      width: '300px',
                      top: `${tooltipPosition.top}px`,
                      left: `${tooltipPosition.left}px`,
                    }}
                  >
                    {comment}
                    {tooltipPosition.arrowPosition === 'top' ? (
                      <div 
                        className="absolute top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100"
                        style={{ left: tooltipPosition.arrowLeft || '20px' }}
                      ></div>
                    ) : (
                      <div 
                        className="absolute bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-100"
                        style={{ left: tooltipPosition.arrowLeft || '20px' }}
                      ></div>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            isEditable && (
              <button
                onClick={handleEdit}
                className="w-full text-left text-sm text-gray-400 hover:text-gray-600 px-2 py-1 border border-dashed border-gray-300 rounded hover:border-gray-400 transition-colors"
              >
                Add comment...
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentComment;

